// The in-app AI (Figma AI / FigJam AI): ask in words, or pick a quick action for the selection.
// The server runs Claude with the same tools agents use, in this file, as you; every step shows
// here as it happens, and the changes appear on the canvas live (undoable, attributed to "AI").
import { useEffect, useRef, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

type Step = { kind: 'note' | 'tool' | 'error'; text: string; ok?: boolean };
interface Turn {
  prompt: string;
  steps: Step[];
  answer: string;
  status: 'running' | 'done' | 'error';
  meta?: string;
}

const TOOL_LABELS: Record<string, string> = {
  status: 'Looked at the file',
  get_tree: 'Read the layers',
  get_nodes: 'Read layers',
  get_selection: 'Read the selection',
  find_nodes: 'Searched layers',
  create_nodes: 'Added layers',
  update_nodes: 'Changed layers',
  delete_nodes: 'Removed layers',
  move_nodes: 'Moved layers',
  arrange: 'Arranged layers',
  screenshot: 'Checked a screenshot',
  batch: 'Made several changes',
  tokens: 'Worked with variables',
  prototype: 'Edited the prototype',
  comments: 'Worked with comments',
  pages: 'Worked with pages',
  select: 'Selected layers',
  focus: 'Showed you the result',
};

/** Quick actions for what's on screen (prompts the person can still edit before sending). */
function quickActions(board: boolean, selected: number): { label: string; prompt: string }[] {
  if (board)
    return selected
      ? [
          { label: 'Summarize', prompt: 'Summarize the selected stickies into a short sticky with the main themes, placed next to them.' },
          { label: 'Sort into groups', prompt: 'Sort the selected stickies into labeled groups by theme, each group in its own section.' },
          { label: 'Make a flowchart', prompt: 'Turn the selected notes into a flowchart: one shape per step, connected in order.' },
        ]
      : [
          { label: 'Brainstorm', prompt: 'Start a brainstorm board about: ' },
          { label: 'Retro board', prompt: 'Make a retro board with "Went well", "To improve" and "Action items" columns.' },
          { label: 'User journey', prompt: 'Make a user journey map for: ' },
        ];
  return selected
    ? [
        { label: 'Rename layers', prompt: 'Rename the selected layers and everything inside them with clear, descriptive names (no "Frame 12" or "Rectangle 3").' },
        { label: 'Shorten text', prompt: 'Rewrite the text in the selection to be shorter and clearer, keeping its meaning and tone.' },
        { label: 'Translate', prompt: 'Translate the text in the selection to German, keeping the layout intact.' },
        { label: 'Dark mode', prompt: 'Make a dark-mode version of the selected frame next to it.' },
        { label: 'Polish', prompt: 'Polish the selection: fix spacing, alignment, contrast and inconsistent styles, without changing its content.' },
        { label: 'Wire prototype', prompt: 'Wire up the prototype: link the buttons and navigation in the selected frames to the screens they should open.' },
      ]
    : [
        { label: 'Design a screen', prompt: 'Design a screen for: ' },
        { label: 'Landing page', prompt: 'Design a landing page hero section for: ' },
        { label: 'Components', prompt: 'Turn repeated elements on this page into components with variants where it helps.' },
      ];
}

export function AssistantPanel() {
  const open = useApp('ui', () => app.ui.assistantOpen);
  const selected = useApp('selection', () => app.editor.selection.length);
  const board = useApp('doc', () => app.pageKind() === 'WHITEBOARD');
  const [available, setAvailable] = useState<boolean | null>(null);
  const [prompt, setPrompt] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const ctl = useRef<AbortController | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const running = turns.at(-1)?.status === 'running';

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    fetch('/api/health')
      .then((r) => r.json())
      .then((h: { ai?: boolean }) => setAvailable(!!h.ai))
      .catch(() => setAvailable(false));
  }, [open]);
  // (block body: newer browsers return a Promise from scrollTo, which React would take for a cleanup)
  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [turns]);
  // closing the panel stops a run
  useEffect(() => () => ctl.current?.abort(), []);

  if (!open) return null;

  const update = (fn: (t: Turn) => Turn) => setTurns((ts) => [...ts.slice(0, -1), fn(ts[ts.length - 1])]);

  const send = async (text = prompt) => {
    const p = text.trim();
    if (!p || running || !app.fileId) return;
    setPrompt('');
    const history = turns.filter((t) => t.status === 'done' && t.answer).flatMap((t) => [{ role: 'user' as const, text: t.prompt }, { role: 'assistant' as const, text: t.answer }]);
    setTurns((ts) => [...ts, { prompt: p, steps: [], answer: '', status: 'running' }]);
    const abort = (ctl.current = new AbortController());
    try {
      const r = await fetch(`/api/files/${app.fileId}/assistant`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ prompt: p, pageId: app.editor.pageId, selection: app.editor.selection, history }), signal: abort.signal });
      if (!r.ok || !r.body) {
        const err = ((await r.json().catch(() => ({}))) as { error?: string }).error ?? r.statusText;
        return update((t) => ({ ...t, status: 'error', steps: [...t.steps, { kind: 'error', text: err }] }));
      }
      const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += value;
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const e = JSON.parse(line) as { type: string; text?: string; delta?: string; name?: string; ok?: boolean; error?: string; message?: string; costUsd?: number; turns?: number; stopped?: string };
          if (e.type === 'note') update((t) => ({ ...t, steps: [...t.steps, { kind: 'note', text: e.text! }] }));
          else if (e.type === 'text') update((t) => ({ ...t, answer: t.answer + e.delta }));
          else if (e.type === 'tool') update((t) => ({ ...t, steps: [...t.steps, { kind: 'tool', text: e.ok ? (TOOL_LABELS[e.name!] ?? e.name!) : `${TOOL_LABELS[e.name!] ?? e.name}: ${e.error ?? 'failed'}`, ok: e.ok }] }));
          else if (e.type === 'error') update((t) => ({ ...t, status: 'error', steps: [...t.steps, { kind: 'error', text: e.message! }] }));
          else if (e.type === 'done') update((t) => ({ ...t, status: 'done', answer: e.text || t.answer, meta: `${e.stopped ? `${e.stopped} · ` : ''}${e.turns} step${e.turns === 1 ? '' : 's'} · about $${(e.costUsd ?? 0).toFixed(2)}` }));
        }
      }
      update((t) => (t.status === 'running' ? { ...t, status: 'done' } : t));
    } catch (err) {
      update((t) => ({ ...t, status: abort.signal.aborted ? 'done' : 'error', meta: abort.signal.aborted ? 'Stopped' : t.meta, steps: abort.signal.aborted ? t.steps : [...t.steps, { kind: 'error', text: (err as Error).message }] }));
    } finally {
      ctl.current = null;
    }
  };

  return (
    <div className="assistant" onKeyDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <div className="assistant-head">
        <span>
          {Icons.sparkle} AI
        </span>
        {turns.length > 0 && !running && (
          <button className="icon-btn" title="New conversation" onClick={() => setTurns([])}>
            ↺
          </button>
        )}
        <button className="icon-btn" title="Close" onClick={() => (ctl.current?.abort(), app.setUI({ assistantOpen: false }))}>
          ✕
        </button>
      </div>
      {available === false && <div className="assistant-off">AI is off on this server. Its admin turns it on by setting ANTHROPIC_API_KEY.</div>}
      <div className="assistant-log" ref={log}>
        {turns.map((t, i) => (
          <div key={i} className="assistant-turn">
            <div className="assistant-prompt">{t.prompt}</div>
            {t.steps.map((s, j) => (
              <div key={j} className={`assistant-step step-${s.kind}${s.ok === false ? ' failed' : ''}`}>
                {s.kind === 'tool' ? (s.ok ? '✓ ' : '✕ ') : s.kind === 'error' ? '! ' : ''}
                {s.text}
              </div>
            ))}
            {t.answer && <div className="assistant-answer">{t.answer}</div>}
            {t.status === 'running' && <div className="assistant-step step-note">Working…</div>}
            {t.meta && <div className="assistant-meta">{t.meta}</div>}
          </div>
        ))}
        {!turns.length && available !== false && (
          <div className="assistant-chips">
            {quickActions(board, selected).map((q) => (
              <button
                key={q.label}
                className="chip"
                onClick={() => {
                  // open-ended ones end in ": " for the person to finish
                  if (q.prompt.endsWith(': ')) {
                    setPrompt(q.prompt);
                    input.current?.focus();
                  } else void send(q.prompt);
                }}
              >
                {q.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="assistant-input">
        <textarea
          ref={input}
          rows={2}
          value={prompt}
          disabled={available === false}
          placeholder={selected ? `Ask about the ${selected} selected layer${selected === 1 ? '' : 's'}…` : board ? 'Ask AI to add to this board…' : 'Describe what to design or change…'}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
            if (e.key === 'Escape') app.setUI({ assistantOpen: false });
          }}
        />
        {running ? (
          <button className="btn secondary" onClick={() => ctl.current?.abort()}>
            Stop
          </button>
        ) : (
          <button className="btn primary" disabled={!prompt.trim() || available === false} onClick={() => void send()}>
            Send
          </button>
        )}
      </div>
    </div>
  );
}
